import { useAuthentication } from '@features/auth/context'
import { AuthenticationStatus } from '@features/auth/types'
import { useChatList } from '@services/chat/queries/useChatList'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

export interface ChatWidgetContext {
  isChatWidgetOpen: boolean
  setIsChatWidgetOpen: Dispatch<SetStateAction<boolean>>
  activeChat: string | undefined
  setActiveChat: Dispatch<SetStateAction<string | undefined>>
  hasNewMessages: boolean
  openLastConversation: (id: string) => void
  saveBarVisible: boolean
  setSaveBarVisible: Dispatch<SetStateAction<boolean>>
}
const ChatWidgetContext = createContext<ChatWidgetContext | null>(null)

export function ChatWidgetContextProvider({ children }: PropsWithChildren) {
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false)
  const [activeChat, setActiveChat] = useState<string | undefined>()
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const [saveBarVisible, setSaveBarVisible] = useState(false)
  const { t } = useTranslation('chat')
  const { loginSatus } = useAuthentication()
  const isLoggedIn = loginSatus === AuthenticationStatus.LoggedIn
  const { data } = useChatList(
    {
      isActive: true,
      limit: 6
    },
    isLoggedIn
  )

  const openLastConversation = useCallback((id: string) => {
    setIsChatWidgetOpen(true)
    setActiveChat(id)
  }, [])

  useEffect(() => {
    // Falls back to false when the data is gone (e.g. cache cleared on
    // logout) so the blinking tab title stops.
    setHasNewMessages(
      isLoggedIn &&
        !!data?.results.some((item) => item.has_new_messages)
    )
  }, [data, isLoggedIn])

  useEffect(() => {
    const originalTitle = document.title
    const newMessageTitle = `✉️(1) - ${t('form.common.new_message')}`
    let intervalId: NodeJS.Timeout | null = null

    if (hasNewMessages) {
      let showOriginal = false
      intervalId = setInterval(() => {
        document.title = showOriginal ? originalTitle : newMessageTitle
        showOriginal = !showOriginal
      }, 1000) // change every 1s
    } else {
      document.title = originalTitle
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      document.title = originalTitle
    }
  }, [t, hasNewMessages])

  return (
    <ChatWidgetContext.Provider
      value={{
        isChatWidgetOpen,
        setIsChatWidgetOpen,
        activeChat,
        setActiveChat,
        hasNewMessages,
        openLastConversation,
        saveBarVisible,
        setSaveBarVisible
      }}
    >
      {children}
    </ChatWidgetContext.Provider>
  )
}

export function useChatWidgetContext() {
  const chatWidgetContext = useContext(ChatWidgetContext)
  if (!chatWidgetContext) {
    throw new Error('ChatWidgetContext was not defined')
  }
  return chatWidgetContext
}
